import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import {
  ILocalOfferWithFiles,
  NLocalOffers,
} from '../../../../interfaces/services/local-offers.interfaces'
import { societyLocalOfferCreateSchema } from '../../../../forms/schemas/society_local_offer_create'
import { CreateUpdateLocalOfferForm } from '../CreateUpdateLocalOfferForm'
import { createLocalOffer, patchLocalOffer } from '../../../../api/local-offers'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'

interface CreateUpdateLocalOfferModalProps {
  show: boolean
  close: () => void
  localOffer?: ILocalOfferWithFiles
  society: ISocietiesModel
}

export const CreateUpdateLocalOfferModal = ({
  localOffer,
  show,
  close,
  society,
}: CreateUpdateLocalOfferModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!localOffer

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        updateMode
          ? 'localOfferView.flashMessage.updateLocalOfferSuccess'
          : 'localOffersView.flashMessage.createLocalOfferSuccess'
      )
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        updateMode
          ? 'localOfferView.flashMessage.updateLocalOfferFailure'
          : 'localOffersView.flashMessage.createLocalOfferFailure'
      )
    )
  }

  const { mutate: mutatePatchLocalOffer, isPending: isPatchingLocalOffer } =
    useMutation({
      mutationFn: patchLocalOffer,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const { mutate: mutateCreateLocalOffer, isPending: isCreatingLocalOffer } =
    useMutation({
      mutationFn: createLocalOffer,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyLocalOfferCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchLocalOffer({
        id: localOffer._id,

        body: data as NLocalOffers.NPatch.IRequestBody,
      })
    } else {
      mutateCreateLocalOffer(data as NLocalOffers.NCreate.IRequestBody)
    }
  }

  const onError = (): void => {
    if (updateMode) {
      showSuccessMessage()
    } else {
      showErrorMessage()
    }
  }

  const loading = isPatchingLocalOffer || isCreatingLocalOffer
  return (
    <Modal show={show} size="lg">
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('localOffersView.modal.create')}
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <CreateUpdateLocalOfferForm
              society={society}
              onError={onError}
              onSubmit={onSubmit}
              onClose={close}
              loading={loading}
              localOffer={localOffer}
            />
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
